import { useLingui } from '@lingui/react/macro';

import config from '@/config';
import Button from '@/design_system/Button/Button';
import Menu, { MenuItem } from '@/design_system/Menu/Menu';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import IconChevron from '@/icons/Chevron.svg';
import IconPrint from '@/icons/Print.svg';
import { ShipmentWithRelations } from '@/models/shipment';

export const PrintShippingLabelButton = ({ shipment }: { shipment: ShipmentWithRelations }) => {
  const { t } = useLingui();

  const hasCustomsFiles = shipment.articles.some((shipmentArticle) =>
    shipmentArticle.article.media?.some((medium) => medium.type === 'customs-file')
  );

  return (
    <Menu
      placement="bottom right"
      trigger={
        <Button
          variant="secondary"
          size="large"
          iconOnly
          style={{ width: 'fit-content', gap: 0 }}
          ariaLabel={t({ id: 'shipment.edit.actions.print', message: 'Print...' })}
          tooltip={t({ id: 'shipment.edit.actions.print', message: 'Print...' })}
        >
          <IconPrint />
          <IconChevron down />
        </Button>
      }
    >
      <PrintShippingLabelMenuItem shipment={shipment} />
      <PrintPackingListMenuItem shipment={shipment} />
      {hasCustomsFiles && <PrintCustomsFilesMenuItem shipment={shipment} />}
    </Menu>
  );
};

export const PrintShippingLabelMenuItem = ({ shipment }: { shipment: { id: string } }) => {
  const { t } = useLingui();
  const label = t({ id: 'shipment.actions.print-shipping-label', message: 'Print shipping label' });

  return (
    <MenuItem
      id="print-shipping-label"
      size="large"
      href={`${config.apiUrl}/shipments/${shipment.id}/media?shippingLabel=1`}
      download
    >
      <Stack row gap="0.5rem" alignItems="center">
        <IconPrint style={{ fontSize: '1.25rem' }} />
        <InlineText>{label}</InlineText>
      </Stack>
    </MenuItem>
  );
};

export const PrintPackingListMenuItem = ({ shipment }: { shipment: { id: string } }) => {
  const { t } = useLingui();
  const label = t({ id: 'shipment.actions.print-packing-list', message: 'Print packing list' });

  return (
    <MenuItem
      id="print-packing-list"
      size="large"
      href={`${config.apiUrl}/shipments/${shipment.id}/media?packingList=1`}
      download
    >
      <Stack row gap="0.5rem" alignItems="center">
        <IconPrint style={{ fontSize: '1.25rem' }} />
        <InlineText>{label}</InlineText>
      </Stack>
    </MenuItem>
  );
};

const PrintCustomsFilesMenuItem = ({ shipment }: { shipment: { id: string } }) => {
  const { t } = useLingui();
  const label = t({ id: 'shipment.actions.print-customs-files', message: 'Print customs files' });

  return (
    <MenuItem
      id="print-customs-files"
      size="large"
      href={`${config.apiUrl}/shipments/${shipment.id}/media?customsFiles=1`}
      download
    >
      <Stack row gap="0.5rem" alignItems="center">
        <IconPrint style={{ fontSize: '1.25rem' }} />
        <InlineText>{label}</InlineText>
      </Stack>
    </MenuItem>
  );
};

export const PrintItemsSheetsButton = ({ shipment }: { shipment: { id: string } }) => {
  const { t } = useLingui();
  const label = t({
    id: 'shipment.actions.print-items-sheets',
    message: 'Print all items tracking sheets',
  });

  return (
    <Button
      variant="secondary"
      size="large"
      iconOnly
      href={`${config.apiUrl}/shipments/${shipment.id}/media?itemsSheets=1`}
      download
      ariaLabel={label}
      tooltip={label}
    >
      <IconPrint />
    </Button>
  );
};

export const PrintItemsSheetsMenuItem = ({ shipment }: { shipment: { id: string } }) => {
  const { t } = useLingui();
  const label = t({
    id: 'shipment.actions.print-items-sheets',
    message: 'Print all items tracking sheets',
  });

  return (
    <MenuItem
      id="print-items-sheets"
      size="large"
      href={`${config.apiUrl}/shipments/${shipment.id}/media?itemsSheets=1`}
      download
    >
      <Stack row gap="0.5rem" alignItems="center">
        <IconPrint style={{ fontSize: '1.25rem' }} />
        <InlineText>{label}</InlineText>
      </Stack>
    </MenuItem>
  );
};
